/**
 * Font sizes for the text annotation tool, and the round-robin the toolbar cycles through.
 *
 * Small is the size annotations have always rendered at (20px, hardcoded in the annotation layer
 * until now), so existing notes keep the appearance they were placed with. Medium and Large step
 * up by 2px each.
 *
 * The cycle is a queue, not a setting: clicking the T icon activates the tool at the size that was
 * next in line and then advances. So the badge on the icon has to say what the NEXT click will
 * give, which is not the same as the size the tool is currently drawing at once it is active.
 */

/** Size in force before this feature existed; keeping it first leaves old notes unchanged. */
export const BASE_TEXT_SIZE = 20;

/**
 * The three tiers: 20 / 28 / 36px.
 *
 * They started at 20/22/24, which was too tight to read as three tiers at all — a 2px step is
 * about 10% and looks identical next to candles and axis labels, so the size was only knowable
 * from the toolbar badge. 20/24/28 was a first widening; this is a second. 8px steps put Medium
 * 40% above Small and Large 80% above it, which is unmistakable on a high-resolution chart where
 * a 20px note is already small relative to the canvas.
 */
export const TEXT_SIZES = [
  { label: 'Small', short: 'S', px: BASE_TEXT_SIZE },
  { label: 'Medium', short: 'M', px: BASE_TEXT_SIZE + 8 },
  { label: 'Large', short: 'L', px: BASE_TEXT_SIZE + 16 },
];

/** Wraps an index into the cycle, tolerating anything stored from an older session. */
export function normaliseSizeIndex(index) {
  const n = Number(index);
  if (!Number.isInteger(n)) return 0;
  return ((n % TEXT_SIZES.length) + TEXT_SIZES.length) % TEXT_SIZES.length;
}

/** The size at an index. */
export function textSizeAt(index) {
  return TEXT_SIZES[normaliseSizeIndex(index)];
}

/** The next index in the round-robin. */
export function nextSizeIndex(index) {
  return normaliseSizeIndex(normaliseSizeIndex(index) + 1);
}

/**
 * Activating the tool: which size to draw at now, and what the queue advances to.
 *
 * Returned together because they must move as one — reading the size and advancing the index in
 * two separate places is how the badge ends up one step out of sync with what gets placed.
 */
export function activateTextTool(currentIndex) {
  const index = normaliseSizeIndex(currentIndex);
  return { activeSize: TEXT_SIZES[index], nextIndex: nextSizeIndex(index) };
}

/**
 * What the drawing mode becomes when a drawing tool's icon is clicked.
 *
 * Every other drawing tool toggles: clicking the active one turns it off. The text tool must not,
 * because a repeat click is how the size cycle advances — toggling would make every second click a
 * no-op and cost two clicks per size step. Esc and placing a note both still clear the mode, so
 * dropping the toggle traps nobody.
 *
 * Lives here rather than inline in the toolbar because the exception exists *only* because of the
 * size cycle, and because a switch statement is not something a test can reach.
 */
/**
 * Tools that share the size cycle, and therefore do NOT toggle off on a repeat click.
 *
 * The text tool set the pattern: clicking it again advances the size rather than switching it off,
 * so stepping through the tiers costs one click each. The circle tools place sized shapes from the
 * same tiers and behave the same way. Esc and placing the shape both still clear the mode.
 */
export const SIZED_TOOLS = ['draw_text', 'draw_circle_empty', 'draw_circle_filled'];

/** Whether a tool draws at one of the shared size tiers. */
export function isSizedTool(action) {
  return SIZED_TOOLS.includes(action);
}

export function drawingModeAfterClick(currentMode, action) {
  if (isSizedTool(action)) return action;
  return currentMode === action ? null : action;
}

/** Tooltip text, e.g. {@code "Text Tool (Medium - 22px)"}. */
export function textToolTooltip(size) {
  const s = size || TEXT_SIZES[0];
  return `Text Tool (${s.label} - ${s.px}px)`;
}
